import { useState, useEffect, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { KnmListPanel } from 'KnmListPackage/KnmListPanelModule/components/KnmListPanel';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IInitialOrder } from 'CorePackage/MainModule/interfaces/IInitialOrder';
import { getStorageItem, setStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { TableColumnSortEnum } from 'CorePackage/MainModule/enums/TableColumnSortEnum';
import { getSortOrderByPeriod } from 'CorePackage/MainModule/common/getSortOrderByPeriod';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';

import { initialKnmLIstTableColumns } from '../constants/initialKnmLIstTableColumns';
import { initialErknmListTableColumns } from '../constants/initialErknmListTableColumns';
import { KnmListTableColumnsModal } from './KnmListTableColumnsModal';
import {
  knmListDeleteKnms,
  knmListGetData,
  knmListGetDataNextPage,
  knmListInitPage,
  knmListRestoreKnms,
  knmListSetOrder,
} from '../actions/knmListActions';
import { getKnmListData, getKnmListTotalCount } from '../selectors/knmListSelectors';
import { IKnmRaw } from '../interfaces/IKnmRaw';
import { KnmListTable } from './KnmListTable';

function getInitialTableColumns(
  knmListOrder: string | undefined,
  selectedTableColumnsIds: Array<ITableColumn<IKnmRaw>['id']> | undefined,
  isErknm: boolean,
) {
  const initialOrderString = knmListOrder;
  const initialOrder: IInitialOrder[] =
    initialOrderString?.split(';').map((orderStringItem: string) => {
      const [id, value] = orderStringItem.split(',');

      return {
        id,
        value,
      } as IInitialOrder;
    }) || [];

  const currentInitialTableColumns = isErknm ? initialErknmListTableColumns : initialKnmLIstTableColumns;

  const initialTableColumns: ITableColumn<IKnmRaw>[] = currentInitialTableColumns.map(initialTableColumn => ({
    ...initialTableColumn,
    sort: initialOrderString
      ? initialOrder.find(orderItem => orderItem.id === initialTableColumn.id)?.value || TableColumnSortEnum.NONE
      : initialTableColumn.sort,
    isSelected: selectedTableColumnsIds
      ? selectedTableColumnsIds.some(selectedTableColumnId => selectedTableColumnId === initialTableColumn.id)
      : initialTableColumn.isSelected,
  }));

  return initialTableColumns;
}

interface IKnmListProps {
  isErknm: boolean;
  knms: IKnmRaw[];
  totalCount: number;
}

const KnmListWithoutStore = ({ isErknm, knms, totalCount }: IKnmListProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>({});
  const idsCheck = useMemo(
    () =>
      Object.keys(itemsCheck)
        .filter(id => itemsCheck[id])
        .map(id => Number(id)),
    [itemsCheck],
  );
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const knmListOrder = getStorageItem<string>(StorageNamesEnum.KNM_LIST_ORDER);
  const selectedTableColumnsIds = getStorageItem<Array<ITableColumn<IKnmRaw>['id']>>(
    StorageNamesEnum.KNM_LIST_TABLE_COLUMNS,
  );
  const [tableColumns, setTableColumns] = useState<ITableColumn<IKnmRaw>[]>(
    getInitialTableColumns(knmListOrder, selectedTableColumnsIds, isErknm),
  );

  useEffect(() => {
    const order = getSortOrder(
      getInitialTableColumns(knmListOrder, selectedTableColumnsIds, isErknm),
      getSortOrderByPeriod,
    );

    dispatch(knmListSetOrder(order));
  }, [dispatch, isErknm, knmListOrder, selectedTableColumnsIds]);

  useEffect(() => {
    dispatch(knmListInitPage());
  }, [dispatch]);

  const handleLoadClick = useCallback(() => dispatch(knmListGetDataNextPage()), [dispatch]);

  const [isKnmListTableColumnsModalVisible, setIsKnmListTableColumnsModalVisible] = useState(false);

  useEffect(() => {
    setTableColumns(getInitialTableColumns(knmListOrder, selectedTableColumnsIds, isErknm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErknm]);

  const order = getSortOrder(tableColumns, getSortOrderByPeriod);

  useEffect(() => {
    setStorageItem(StorageNamesEnum.KNM_LIST_ORDER, order);
  }, [order]);

  useEffect(() => {
    const newSelectedTableColumnsIds = tableColumns.reduce(
      (tableColumnsIds: Array<ITableColumn<IKnmRaw>['id']>, tableColumn) => {
        if (tableColumn?.isSelected !== false) {
          tableColumnsIds.push(tableColumn.id);
        }

        return tableColumnsIds;
      },
      [],
    );

    setStorageItem(StorageNamesEnum.KNM_LIST_TABLE_COLUMNS, newSelectedTableColumnsIds);
  }, [tableColumns]);

  const handleDeleteClick = useCallback(() => {
    setIsConfirmModal(false);
    dispatch(knmListDeleteKnms(idsCheck));
  }, [dispatch, idsCheck]);

  const handleDeleteModalClose = useCallback(() => setIsConfirmModal(false), []);

  const handleDeleteModalOpen = useCallback(() => setIsConfirmModal(true), []);

  const handleRestoreClick = useCallback(() => dispatch(knmListRestoreKnms(idsCheck)), [dispatch, idsCheck]);

  const handleSettingsButtonClick = useCallback(() => setIsKnmListTableColumnsModalVisible(true), []);

  const closeKnmListTableColumnsModal = useCallback(() => setIsKnmListTableColumnsModalVisible(false), []);

  const onChangeSort = useCallback(
    (newColumnsData: ITableColumn<IKnmRaw>[]) => {
      const newOrder = getSortOrder(newColumnsData, getSortOrderByPeriod);

      dispatch(knmListSetOrder(newOrder));
      dispatch(knmListGetData());
    },
    [dispatch],
  );

  return (
    <TablePage>
      <KnmListPanel
        dataCount={totalCount}
        columns={tableColumns}
        onDeleteClick={handleDeleteModalOpen}
        onRestoreClick={handleRestoreClick}
        itemsCheck={itemsCheck}
        knms={knms}
      />
      <KnmListTable
        tableColumns={tableColumns}
        itemsCheck={itemsCheck}
        knms={knms}
        totalCount={totalCount}
        isConfirmModalVisible={isConfirmModal}
        setItemsCheck={setItemsCheck}
        setTableColumns={setTableColumns}
        handleLoadClick={handleLoadClick}
        onChangeSort={onChangeSort}
        handleDeleteClick={handleDeleteClick}
        handleDeleteModalClose={handleDeleteModalClose}
        handleSettingsButtonClick={handleSettingsButtonClick}
      />
      <KnmListTableColumnsModal
        visible={isKnmListTableColumnsModalVisible}
        tableItems={tableColumns}
        onApply={setTableColumns}
        onClose={closeKnmListTableColumnsModal}
      />
    </TablePage>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  knms: getKnmListData(state),
  totalCount: getKnmListTotalCount(state),
});

export const KnmList = connect(mapStateToProps)(KnmListWithoutStore);
